import { DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link, request as umiRequest } from '@umijs/max';
import { Button, Modal, Segmented, Tag, message } from 'antd';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  GrokIcon,
  Sora2Icon,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { clearAuthToken, getAuthToken } from './authToken';
import {
  applyThemeToDom,
  getStoredThemeMode,
  getThemeSettings,
  setStoredThemeMode,
} from './theme';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const desktopAuthPath = '/desktop-auth';
const APP_MODE_KEY = 'qn_app_mode';
type AppMode = 'agent' | 'geo';
const getStoredAppMode = (): AppMode => {
  if (typeof localStorage === 'undefined') return 'agent';
  const v = localStorage.getItem(APP_MODE_KEY);
  return v === 'geo' ? 'geo' : 'agent';
};
const setStoredAppMode = (mode: AppMode) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(APP_MODE_KEY, mode);
};
const isGeoPath = (pathname?: string) => String(pathname || '').startsWith('/geo');

type ApiResponse<T> = {
  success: boolean;
  data: T;
  errorCode?: string | null;
  errorMessage?: string | null;
};

type AuthClientReleaseLatestVO = {
  recordId: number;
  platform: string;
  arch?: string;
  version: string;
  downloadUrl: string;
  uploadedAt?: string | null;
};

const detectAuthClientPlatform = (): 'win' | 'mac' => {
  const ua = String(navigator.userAgent || '').toLowerCase();
  const pf = String(navigator.platform || '').toLowerCase();
  const v = `${ua} ${pf}`;
  if (v.includes('windows')) return 'win';
  if (v.includes('mac') || v.includes('os x') || v.includes('iphone') || v.includes('ipad')) return 'mac';
  return 'win';
};

const fetchLatestAuthClient = async (platform: 'win' | 'mac', arch?: 'arm64' | 'x64') => {
  try {
    const resp = await umiRequest<ApiResponse<AuthClientReleaseLatestVO>>('/api/auth-client/releases/latest', {
      method: 'GET',
      params: { platform, arch },
    } as any);
    // 期望后端返回：{ success: boolean, data: { downloadUrl, ... } }
    const anyResp = resp as any;
    const success = typeof anyResp?.success === 'boolean' ? anyResp.success : anyResp?.data?.success;
    const url = anyResp?.data?.downloadUrl ?? anyResp?.data?.data?.downloadUrl;
    if (!success || !url) {
      message.warning('暂无可下载的最新版安装包');
      return null;
    }
    return url as string;
  } catch {
    message.error('获取安装包失败');
    return null;
  }
};

const downloadLatestAuthClient = async () => {
  const platform = detectAuthClientPlatform();
  if (platform === 'win') {
    const url = await fetchLatestAuthClient('win', 'x64');
    if (url) window.location.href = url;
    return;
  }

  Modal.confirm({
    title: '下载桌面端（Mac）',
    content: '请选择适配你的 Mac 架构的安装包：Apple Silicon(arm64) 或 Intel(x64)。',
    okText: 'Apple Silicon (arm64)',
    cancelText: 'Intel (x64)',
    onOk: async () => {
      const url = await fetchLatestAuthClient('mac', 'arm64');
      if (url) window.location.href = url;
    },
    onCancel: async () => {
      const url = await fetchLatestAuthClient('mac', 'x64');
      if (url) window.location.href = url;
    },
  });
};

const applyAppModeToDom = (mode: AppMode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.app = mode;
};
const filterMenuByMode = (items: any[], mode: AppMode): any[] => {
  const keepGeo = mode === 'geo';
  const walk = (arr: any[]): any[] =>
    (arr || [])
      .map((it) => {
        const children = Array.isArray(it.children) ? walk(it.children) : it.children;
        return { ...it, children };
      })
      .filter((it) => {
        const p = String(it.path || '');
        // layout=false 的 /user/*、* 不会进菜单；这里只处理业务菜单
        const geo = p.startsWith('/geo');
        return keepGeo ? geo : !geo;
      });
  return walk(items);
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result', desktopAuthPath].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const mode = getStoredThemeMode();
    // 初始化时同步到 DOM（用于 CSS 变量主题）
    if (typeof document !== 'undefined') applyThemeToDom(mode);
    return {
      fetchUserInfo,
      currentUser,
      settings: {
        ...(defaultSettings as Partial<LayoutSettings>),
        ...getThemeSettings(mode),
      },
    };
  }
  const mode = getStoredThemeMode();
  if (typeof document !== 'undefined') applyThemeToDom(mode);
  return {
    fetchUserInfo,
    settings: {
      ...(defaultSettings as Partial<LayoutSettings>),
      ...getThemeSettings(mode),
    },
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const currentMode = getStoredThemeMode();
  const currentAppMode = getStoredAppMode();
  const points = (initialState?.currentUser as any)?.points ?? 620;
  // 初始化时把 AppMode 同步到 DOM（用于 GEO 专属主题）
  applyAppModeToDom(currentAppMode);

  const appModeSegmented = (
    <Segmented
      key="appMode"
      className="qn-topnav-seg qn-topnav-seg-appmode"
      size="small"
      value={currentAppMode}
      options={[
        { label: 'Agent', value: 'agent' },
        { label: 'GEO', value: 'geo' },
      ]}
      onChange={(v) => {
        const next = (String(v) as AppMode) === 'geo' ? 'geo' : 'agent';
        setStoredAppMode(next);
        applyAppModeToDom(next);
        const to = next === 'geo' ? '/geo/home' : '/home';
        if (history.location.pathname !== to) {
          history.push(to);
        }
      }}
    />
  );
  return {
    // 菜单直接使用 routes.ts 的 name（不走 i18n key），避免出现 menu.xxx 缺失提示
    menu: { locale: false },
    headerContentRender: () => appModeSegmented,
    actionsRender: () => [
      <Tag
        key="points"
        className="qn-topnav-points"
        style={{
          height: 34,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 14px',
          borderRadius: 999,
          border: '1px solid var(--app-border)',
          background: 'color-mix(in srgb, var(--app-primary) 10%, transparent)',
          color: 'var(--app-text)',
          fontWeight: 700,
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'linear-gradient(135deg, color-mix(in srgb, var(--app-primary) 92%, #a855f7) 0%, color-mix(in srgb, var(--app-primary) 78%, #22d3ee) 100%)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          $
        </span>
        <span style={{ fontSize: 16, letterSpacing: 0.2 }}>{points}</span>
        <span style={{ fontSize: 13, color: 'var(--app-text-secondary)', fontWeight: 600 }}>
          积分
        </span>
      </Tag>,
      <Segmented
        key="theme"
        className="qn-topnav-seg qn-topnav-seg-theme"
        size="small"
        value={currentMode}
        options={[
          { label: '浅色科技', value: 'light-tech' },
          { label: '浅色默认', value: 'light' },
          { label: '深色科技', value: 'dark-tech' },
        ]}
        onChange={(v) => {
          const mode = String(v) as any;
          setStoredThemeMode(mode);
          applyThemeToDom(mode);
          setInitialState((pre) => ({
            ...pre,
            settings: {
              ...(pre?.settings || {}),
              ...getThemeSettings(mode),
            },
          }));
        }}
      />,
      <Button
        key="authClientDownload"
        type="text"
        icon={<DownloadOutlined />}
        onClick={() => void downloadLatestAuthClient()}
      >
        下载
      </Button>,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: false,
    onPageChange: () => {
      const { location } = history;
      const appMode = getStoredAppMode();
      applyAppModeToDom(appMode);
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath &&
        location.pathname !== desktopAuthPath
      ) {
        history.push(loginPath);
        return;
      }
      // Agent/GEO 模式下，强制停留在各自的路由空间
      if (location.pathname !== loginPath) {
        if (appMode === 'geo' && !isGeoPath(location.pathname)) {
          history.push('/geo/home');
          return;
        }
        if (appMode === 'agent' && isGeoPath(location.pathname)) {
          history.push('/home');
          return;
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => {
      const appMode = getStoredAppMode();
      const patch = (items: any[]): any[] =>
        items.map((it) => {
          const children = Array.isArray(it.children) ? patch(it.children) : it.children;
          if (it.path === '/grok-video') {
            return { ...it, icon: <GrokIcon />, children };
          }
          if (it.path === '/sora2-video') {
            return { ...it, icon: <Sora2Icon />, children };
          }
          return { ...it, children };
        });
      return filterMenuByMode(patch(menuData as any), appMode);
    },
    // 不需要每个页面上方的 Ant PageHeader 标题栏
    pageHeaderRender: false,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  requestInterceptors: [
    (url, options) => {
      const token = getAuthToken();
      if (token) {
        const headers = {
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`,
        };
        return {
          url,
          options: {
            ...options,
            headers,
          },
        };
      }
      return { url, options };
    },
  ],
  responseInterceptors: [
    async (response) => {
      if (response.status === 401) {
        clearAuthToken();
        if (history.location.pathname !== loginPath) {
          history.push(loginPath);
        }
      }
      return response;
    },
  ],
};
